/**
 * src/pages/BillingSettings.jsx
 * Suscripciones: la operación de cobros de los tenants, en tres pestañas.
 *
 * Rediseño del 28-08 (hilo de distribución), decidido por Manuel:
 *   · Fuera la tarjeta «Suscripción actual»: describía la suscripción del
 *     usuario logueado, y al panel solo entra el admin — esa audiencia
 *     «cliente» no existe (los clientes entran por /acceder a su dashboard).
 *   · Fuera «Diagnóstico Flow» y «Probar trial»: nacieron para la validación
 *     de Flow (julio-agosto) y su propósito está cumplido — el trial Opción B
 *     está implementado y la reconciliación cubre la inspección operativa.
 *     Los endpoints del backend siguen existiendo; solo sale la puerta visual.
 *   · Tres pestañas por naturaleza: Operación (recurrente) · Rescates
 *     (excepcional, trato de zona de riesgo) · Diagnóstico (la sonda).
 *   · «Forzar activación» sale de dentro de Reconciliar: un forzado
 *     excepcional no puede vivir escondido en una herramienta rutinaria.
 *
 * La ruta sigue siendo /settings/billing (las alertas de negocio de Estado
 * aterrizan aquí); solo cambian el rótulo del menú y el contenido.
 */
import { useState } from 'react'
import { Header } from '../components/layout/Header'
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { BarraPestanas } from '../components/ui/Pestanas'
import { verificarPlanesFlow, reconciliarFlow, runMantenimiento, reasignarSuscripcion } from '../api/billing'

const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: 'var(--text-base)',
  fontFamily: 'var(--font-mono)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)', background: 'var(--color-surface-2)',
  color: 'var(--color-text)', boxSizing: 'border-box',
}

// ── Operación: lo recurrente, primero ────────────────────────────────────────

/**
 * Por qué el resumen sale como sale.
 *
 * Manuel, 06-09-2026: pulsó el botón sobre un tenant que sabía impagado y recibió
 * `fin_periodo: 0`. Ese cero puede significar cuatro cosas muy distintas, y sin
 * distinguirlas hay que ir tenant a tenant a mano. Es el §3 de
 * ERRORES_RECURRENTES: un cero que no dice si es «no hay trabajo» o «no he
 * podido». Aquí se dice, en palabras y en el orden en que el job mira las cosas.
 */
/**
 * La explicación de UNA población (las de pago, o las que están en prueba).
 *
 * El orden de los `if` es el orden en que el job descarta cada fila: lo primero
 * que se lee es lo primero que hay que arreglar. Y cuando hay vencidas de verdad,
 * el motivo sale de `descartes` —lo que el bucle contó al hablar con Flow— en vez
 * de suponerlo: era la parte que faltaba, y la que estaba en un log que nadie lee.
 */
function _porQue(poblacion, c, descartes) {
  const d = descartes || {}
  if (c.vencidas > 0) {
    // El PRIMERO de la lista, y no por gravedad: es el único que no se arregla
    // solo. Los otros tres son estados de paso —la factura aún no existe, el
    // enlace todavía no está— que se resuelven en la siguiente vuelta. Este da
    // exactamente lo mismo mañana, y hasta el 07-09-2026 se contaba como «la
    // consulta a Flow falló», que es lo contrario de lo que pasó: Flow respondió.
    if (d.no_existe_en_flow > 0) {
      // ⚠️ El final NO es el mismo para las dos poblaciones (Manuel, 21-09-2026).
      // Acababa prometiendo a ambas que no se había cortado nada, y para una
      // prueba era falso: el cierre sin factura corre detrás y le programa la
      // baja, exista o no en Flow. A quien ya pagó, en cambio, no se le corta.
      const despues = poblacion === 'de pago'
        ? 'A quien ya pagó NO se le corta por esto: se le avisa de la renovación y el corte se decide a mano en «Zona de riesgo» de su ficha.'
        : 'Eso NO deja la prueba abierta: el cierre sin factura le avisa con el enlace para activar su plan, la marca como impagada y le programa la baja tras los días de gracia (lo verás en «fin_trial_sin_factura»). Si el fallo es nuestro, anula esa baja en «Zona de riesgo» de su ficha antes de que venza.'
      return `Flow responde que ${d.no_existe_en_flow} de las ${c.vencidas} suscripción(es) ${poblacion} vencida(s) NO EXISTEN en el entorno que estamos consultando. Reintentar no lo arregla: o el identificador se creó en el otro entorno de Flow (sandbox frente a real), o allí se borró. Mira cuál está activo en la pestaña «Diagnóstico» de esta misma pantalla, tarjeta «Planes en Flow (sonda)». ${despues}`
    }
    if (d.flow_no_responde > 0) {
      // ⚠️ Sin decir de quién es el problema. La primera versión afirmaba «si
      // sigue igual, el problema está en Flow, no aquí» — y eso no se sabe: la
      // consulta puede fallar por Flow caído, por credenciales, o porque esa
      // suscripción no existe en el entorno que estamos consultando. Se enseña
      // lo que Flow contestó y se deja el juicio a quien lo lee.
      return `Hay ${c.vencidas} suscripción(es) ${poblacion} vencida(s), pero la consulta a Flow falló (${d.flow_no_responde}).`
    }
    if (d.sin_enlace_de_pago > 0) {
      return `Hay ${c.vencidas} suscripción(es) ${poblacion} vencida(s) con una factura impagada, pero Flow no da todavía su enlace de pago (${d.sin_enlace_de_pago}). El job reintenta a diario: sin enlace, avisar al cliente lo dejaría sin forma de pagar.`
    }
    if (d.no_se_pudo_avisar > 0) {
      return `Hay ${c.vencidas} suscripción(es) ${poblacion} vencida(s) con su enlace de pago listo, pero el aviso al cliente no salió (${d.no_se_pudo_avisar}) — revisa RESEND_API_KEY. NO se les ha marcado el impago ni programado la baja: no se corta a quien no ha sido avisado. Se reintenta en la siguiente vuelta.`
    }
    if (d.sin_invoice_impagado > 0) {
      return `Hay ${c.vencidas} suscripción(es) ${poblacion} vencida(s), pero Flow aún no ha emitido la factura del periodo siguiente (${d.sin_invoice_impagado}). Suele tardar unas horas; el job reintenta a diario.`
    }
    return `Hay ${c.vencidas} suscripción(es) ${poblacion} vencida(s) que no se han procesado y el motivo no ha quedado registrado. Esto no debería pasar: mira los logs del servicio.`
  }
  if (c.sin_periodo_fin > 0) {
    return `Hay ${c.sin_periodo_fin} suscripción(es) ${poblacion} SIN fecha de fin de periodo, así que el job no puede saber si vencieron. Pulsa «Reconciliar suscripciones» aquí debajo: trae esa fecha desde Flow.`
  }
  if (c.sin_subscription_id > 0) {
    return `Hay ${c.sin_subscription_id} suscripción(es) ${poblacion} sin identificador de Flow. No se pueden consultar ni cobrar desde aquí.`
  }
  if (c.aun_vigentes > 0) {
    return `Las ${c.aun_vigentes} suscripción(es) ${poblacion} están al corriente: su periodo no ha vencido.`
  }
  return `No hay ninguna suscripción ${poblacion}.`
}

/**
 * Por qué una prueba vencida NO se cerró. El paso corre al margen de Flow —cerrar
 * el ciclo de un cliente no puede depender de que un tercero tenga lista una
 * factura—, así que sus motivos son otros y se explican aparte.
 *
 * Los tres significan lo mismo para el cliente: **no se le ha avisado, y por eso
 * tampoco se le ha empezado a contar la gracia**. No se corta a quien no sabe.
 */
function _avisosPendientes(m, poblacion = 'prueba vencida') {
  const d = m || {}
  const lineas = []
  if (d.sin_url_de_activacion > 0) {
    lineas.push(`Hay ${d.sin_url_de_activacion} ${poblacion}(s) sin avisar porque FRONTEND_URL no está configurada: sin ella no hay a dónde mandar al cliente a activar su plan. No se ha enviado nada ni se ha programado ninguna baja.`)
  }
  if (d.no_se_pudo_enviar > 0) {
    lineas.push(`${d.no_se_pudo_enviar} aviso(s) de ${poblacion} no se pudieron enviar (revisa RESEND_API_KEY). No se ha tocado nada más: no se actúa sobre quien no ha sido avisado. Se reintenta en la siguiente vuelta.`)
  }
  if (d.sin_usuario > 0) {
    lineas.push(`${d.sin_usuario} ${poblacion}(s) sin usuario con correo al que avisar: tampoco se actúa sobre ellos, porque una fila sin usuario es un problema de datos nuestro y no una deuda del cliente.`)
  }
  return lineas
}

function PorQueEseCero({ d, r }) {
  const lineas = []
  const a = d.active || {}
  const t = d.trialing || {}

  if (!d.suscripciones) {
    lineas.push('No hay ninguna suscripción registrada: no hay nada que este job pueda tocar.')
  } else {
    // Las dos poblaciones se explican IGUAL. La primera versión (06-09) solo
    // razonaba sobre las de pago y de las pruebas soltaba un recuento seco:
    // Manuel vio «1 vencida» junto a `fin_trial: 0` y el porqué seguía sin estar.
    // Un diagnóstico que explica la mitad de los casos deja la otra mitad
    // exactamente igual de muda que antes.
    if (r.fin_periodo === 0) lineas.push(_porQue('de pago', a, r.descartes))
    if (r.fin_trial === 0) lineas.push(_porQue('en prueba gratuita', t, r.descartes))
  }

  const estados = Object.entries(d.por_estado || {})
    .map(([k, v]) => `${k} ${v}`).join(' · ')

  return (
    <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-3)',
                  borderTop: '1px solid var(--color-border)',
                  fontSize: 'var(--text-sm)', color: 'var(--color-text-2)' }}>
      {lineas.map((l, i) => <div key={i} style={{ marginBottom: 'var(--space-1)' }}>{l}</div>)}
      {/* Por qué un aviso de fin de prueba no llegó a salir. Mismo criterio que
          los descartes del cobro: un contador a cero sin motivo obliga a ir a los
          logs, y estos tres motivos son accionables sin salir de aquí. */}
      {_avisosPendientes(r.sin_factura_motivos).map((l, i) => (
        <div key={`av${i}`} style={{ marginBottom: 'var(--space-1)' }}>{l}</div>
      ))}
      {/* Los del gemelo que avisa y no corta. Misma forma, otra población. */}
      {_avisosPendientes(r.renovacion_motivos, 'renovación vencida').map((l, i) => (
        <div key={`rv${i}`} style={{ marginBottom: 'var(--space-1)' }}>{l}</div>
      ))}
      {/* Lo que Flow contestó, literal. Es lo que distingue «está caído» de «esa
          suscripción no existe» o «la clave no vale», que piden cosas distintas. */}
      {(r.descartes_detalle || []).length > 0 && (
        <div style={{ marginTop: 'var(--space-2)', padding: 'var(--space-2)',
                      background: 'var(--color-surface-2)',
                      borderRadius: 'var(--radius-sm)',
                      fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
                      color: 'var(--color-text-2)', wordBreak: 'break-word' }}>
          {r.descartes_detalle.map((x, i) => <div key={i}>{x}</div>)}
        </div>
      )}
      <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)',
                    color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
        {d.suscripciones} suscripción(es) en total{estados ? ` · ${estados}` : ''}
      </div>
    </div>
  )
}

function Mantenimiento() {
  const [mant, setMant] = useState(null)
  const [cargando, setCargando] = useState(false)

  async function ejecutar() {
    setCargando(true); setMant(null)
    try {
      setMant(await runMantenimiento())
    } catch (e) {
      setMant({ error: e.response?.data?.detail || 'No se pudo ejecutar el mantenimiento.' })
    } finally { setCargando(false) }
  }

  return (
    <Card title="Ejecutar mantenimiento (job diario)"
          subtitle="Corre ahora fin de trial, bajas programadas, preavisos y limpieza. Idempotente.">
      <Button onClick={ejecutar} loading={cargando}>Ejecutar mantenimiento</Button>
      {mant?.error && (
        <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)', color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>✗ {mant.error}</div>
      )}
      {mant?.diagnostico && <PorQueEseCero d={mant.diagnostico} r={mant} />}
      {mant && !mant.error && (
        <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-base)', fontFamily: 'var(--font-mono)', color: 'var(--color-text)' }}>
          {/* Por TIPO, no por lista negra. La lista se olvidaba: primero se coló
              `diagnostico`, y el 07-09 volvió a pasar con `aviso_motivos` —«[object
              Object]» en la pantalla de Manuel— porque añadir un dato al resumen
              y acordarse de excluirlo aquí son dos actos separados, y el segundo
              no tiene quien lo recuerde. Esta línea es para cifras: lo que no sea
              un número o un texto se explica debajo, en palabras. */}
          {Object.entries(mant).filter(([, v]) => typeof v === 'number' || typeof v === 'string')
                 .map(([k, v]) => `${k}: ${v}`).join(' · ')}
        </div>
      )}
    </Card>
  )
}

function Reconciliar() {
  const [recon, setRecon] = useState(null)
  const [cargando, setCargando] = useState(false)

  async function reconciliar() {
    setCargando(true); setRecon(null)
    try {
      setRecon(await reconciliarFlow())
    } catch (e) {
      setRecon({ error: e.response?.data?.detail || 'No se pudo reconciliar con Flow.' })
    } finally { setCargando(false) }
  }

  return (
    <Card title="Reconciliar con Flow"
          subtitle="Consulta Flow y activa las suscripciones ya pagadas cuyo webhook no llegó. Idempotente."
          style={{ marginTop: 'var(--space-4)' }}>
      <Button onClick={reconciliar} loading={cargando}>Reconciliar suscripciones</Button>
      {recon && !recon.error && (
        <div style={{ marginTop: '14px' }}>
          <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-text)', marginBottom: '10px' }}>
            Revisadas: <b>{recon.revisadas}</b> · Activadas: <b style={{ color: 'var(--color-ok)' }}>{recon.activadas}</b>
          </div>
          {(recon.detalle || []).map((d, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '7px 0', borderBottom: '1px solid var(--color-border)', fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)' }}>
              <span style={{ color: 'var(--color-text-2)', wordBreak: 'break-all' }}>{d.subscription_id}</span>
              <span style={{ color: d.resultado === 'activada' ? 'var(--color-ok)' : 'var(--color-text-3)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                {d.resultado === 'activada' ? `✓ activada${d.periodo_fin ? ' · hasta ' + d.periodo_fin.slice(0, 10) : ''}` : d.resultado}
              </span>
            </div>
          ))}
        </div>
      )}
      {recon?.error && (
        <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)', color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>✗ {recon.error}</div>
      )}
    </Card>
  )
}

// ── Rescates: excepcional y con consecuencias — trato de zona de riesgo ─────

function Reasignar() {
  const [subId, setSubId] = useState('')
  const [destino, setDestino] = useState('')
  const [darDeBaja, setDarDeBaja] = useState(true)
  const [reas, setReas] = useState(null)
  const [cargando, setCargando] = useState(false)

  async function reasignar() {
    const sid = subId.trim(), dst = destino.trim()
    if (!sid || !dst) {
      setReas({ error: 'Indica el subscription_id (sus_...) y el slug del tenant destino.' }); return
    }
    if (!window.confirm(
      `Vas a mover la suscripción ${sid} al tenant "${dst}"` +
      (darDeBaja ? ' y dar de baja el tenant de origen' : '') +
      '. El comprador entrará en el nuevo tenant al hacer login. ¿Continuar?')) return
    setCargando(true); setReas(null)
    try {
      setReas(await reasignarSuscripcion(sid, dst, darDeBaja))
    } catch (e) {
      setReas({ error: e.response?.data?.detail || 'No se pudo reasignar la suscripción.' })
    } finally { setCargando(false) }
  }

  return (
    <Card title="Reasignar suscripción a otro tenant"
          subtitle="Repunta una suscripción pagada (y sus usuarios) a un tenant nuevo, conservando el pago de Flow. El tenant destino debe existir ya.">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <input value={subId} onChange={e => setSubId(e.target.value)}
               placeholder="subscription_id (sus_xxxxxxxxxx)" style={inputStyle} />
        <input value={destino} onChange={e => setDestino(e.target.value)}
               placeholder="slug del tenant destino (p.ej. 1178733)" style={inputStyle} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-sm)', color: 'var(--color-text-2)' }}>
          <input type="checkbox" checked={darDeBaja} onChange={e => setDarDeBaja(e.target.checked)} />
          Dar de baja el tenant de origen (recomendado)
        </label>
        <div>
          <Button onClick={reasignar} loading={cargando} variant="secondary">Reasignar suscripción</Button>
        </div>
      </div>
      {reas?.error && (
        <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)', color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>✗ {reas.error}</div>
      )}
      {reas && !reas.error && (
        <div style={{ marginTop: '14px', fontSize: 'var(--text-base)', fontFamily: 'var(--font-mono)', color: 'var(--color-text)' }}>
          <div>✓ Suscripción <b>{reas.subscription_id}</b> → tenant <b>{reas.cliente_destino}</b></div>
          <div style={{ color: 'var(--color-text-2)', marginTop: 'var(--space-1)' }}>
            Plan: {reas.tier || '—'} · Estado: {reas.estado_suscripcion || '—'}
            {reas.origen_desactivado ? ` · Origen dado de baja: ${reas.origen_desactivado}` : ''}
          </div>
          <div style={{ color: 'var(--color-text-2)', marginTop: 'var(--space-1)' }}>
            Usuarios movidos: {(reas.usuarios_movidos || []).join(', ') || '—'}
          </div>
        </div>
      )}
    </Card>
  )
}

function ForzarActivacion() {
  const [subId, setSubId] = useState('')
  const [resultado, setResultado] = useState(null)
  const [cargando, setCargando] = useState(false)

  async function forzar() {
    if (!subId.trim()) {
      setResultado({ error: 'Para forzar, indica el subscription_id (sus_...).' }); return
    }
    if (!window.confirm(
      `Vas a ACTIVAR ${subId.trim()} aunque Flow no confirme el pago. ` +
      'Úsalo solo con un cargo verificado en el banco. ¿Continuar?')) return
    setCargando(true); setResultado(null)
    try {
      setResultado(await reconciliarFlow(subId.trim(), true))
    } catch (e) {
      setResultado({ error: e.response?.data?.detail || 'No se pudo forzar la activación.' })
    } finally { setCargando(false) }
  }

  return (
    <Card title="Forzar activación (pago verificado fuera de Flow)"
          subtitle="Activa la suscripción aunque Flow la reporte impaga. Solo con el cargo verificado en el banco (discrepancia Flow↔Webpay). Queda registrado."
          style={{ marginTop: 'var(--space-4)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <input value={subId} onChange={e => setSubId(e.target.value)}
               placeholder="sus_xxxxxxxxxx" style={inputStyle} />
        <div>
          <Button onClick={forzar} loading={cargando} variant="secondary">Forzar activación</Button>
        </div>
      </div>
      {resultado?.error && (
        <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)', color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>✗ {resultado.error}</div>
      )}
      {resultado && !resultado.error && (
        <div style={{ marginTop: '14px', fontSize: 'var(--text-base)', color: 'var(--color-text)' }}>
          Revisadas: <b>{resultado.revisadas}</b> · Activadas: <b style={{ color: 'var(--color-ok)' }}>{resultado.activadas}</b>
        </div>
      )}
    </Card>
  )
}

function Rescates() {
  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)',
        marginBottom: 'var(--space-3)', flexWrap: 'wrap',
      }}>
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
          letterSpacing: '0.13em', textTransform: 'uppercase',
          color: 'var(--color-error)',
        }}>
          Zona de riesgo
        </span>
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)' }}>
          Mueven dinero o tenants. Cada acción pide confirmación y queda registrada.
        </span>
      </div>
      <div style={{
        border: '1px solid var(--color-error-line)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4) var(--space-5)',
      }}>
        <Reasignar />
        <ForzarActivacion />
      </div>
    </div>
  )
}

// ── Diagnóstico: la sonda de planes ─────────────────────────────────────────

function SondaPlanes() {
  const [sonda, setSonda] = useState(null)
  const [cargando, setCargando] = useState(false)

  async function verificar() {
    setCargando(true); setSonda(null)
    try {
      setSonda(await verificarPlanesFlow())
    } catch (e) {
      setSonda({ error: e.response?.data?.detail || 'No se pudo consultar Flow.' })
    } finally { setCargando(false) }
  }

  return (
    <Card title="Planes en Flow (sonda)"
          subtitle="Comprueba que los 6 planes están configurados. No mueve dinero.">
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: sonda ? '14px' : 0 }}>
        <Button onClick={verificar} loading={cargando}>Verificar planes</Button>
        {sonda && !sonda.error && (
          <>
            <Badge color={sonda.entorno === 'sandbox' ? 'blue' : 'yellow'}>
              Entorno: {sonda.entorno === 'sandbox' ? 'sandbox (pago de prueba)' : 'REAL (cobra dinero)'}
            </Badge>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-2)', fontFamily: 'var(--font-mono)' }}>{sonda.resumen}</span>
          </>
        )}
        {/* Cierre del resultado una vez visto (Manuel, 28-08): la lista de
            planes es larga y, leída, estorba. */}
        {sonda && (
          <Button size="sm" variant="ghost" onClick={() => setSonda(null)}>
            Ocultar resultado
          </Button>
        )}
      </div>

      {sonda?.error && (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>✗ {sonda.error}</div>
      )}

      {sonda?.planes && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {sonda.planes.map((p) => {
            const ok = p.estado === 'ok' && p.activo_en_flow
            const color = ok ? 'var(--color-ok)' : 'var(--color-error)'
            return (
              <div key={p.env_var} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--color-border)', gap: '10px' }}>
                <span style={{ fontSize: 'var(--text-base)', textTransform: 'capitalize' }}>{p.tier} · {p.ciclo}</span>
                <span style={{ fontSize: 'var(--text-sm)', color, fontFamily: 'var(--font-mono)', textAlign: 'right' }}>
                  {ok
                    ? `✓ ${p.monto ? p.monto.toLocaleString('es-CL') + ' ' + (p.moneda || '') : 'activo'}`
                    : `✗ ${p.estado}${p.detalle ? ' — ' + p.detalle : ''}`}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </Card>
  )
}

// ── ensamblado ──────────────────────────────────────────────────────────────

const PESTANAS = [
  { clave: 'operacion', label: 'Operación' },
  { clave: 'rescates', label: 'Rescates' },
  { clave: 'diagnostico', label: 'Diagnóstico' },
]

export default function BillingSettings() {
  const [pestana, setPestana] = useState('operacion')

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header
        title="Suscripciones"
        subtitle="Operación de cobros de los tenants (Flow)"
      />

      <BarraPestanas pestanas={PESTANAS} activa={pestana} onCambio={setPestana} />

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
        <div style={{ maxWidth: '900px' }}>
          {pestana === 'operacion' && (
            <>
              <Mantenimiento />
              <Reconciliar />
            </>
          )}
          {pestana === 'rescates' && <Rescates />}
          {pestana === 'diagnostico' && <SondaPlanes />}
        </div>
      </div>
    </div>
  )
}
